import { useEffect, useState, useRef } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  Product,
  ScannerInputValue,
  scannerInputValueInitial,
} from "@/interfaces";
import { formatAxiosError } from "@/utils";
import { useDataApi } from "@/hooks";
import { useCart, useSettings, useApp, useDealer } from "@/contexts";
import { CartHelper } from "@/helpers";
import { CircularIndeterminate, ScannerInput, ErrorMessage } from "../../atoms";
import ProductTable from "../ProductTable";
import * as S from "./styles";

const getNewData = (
  existingData: Product[],
  receivedData: Product[]
): Product[] => {
  return Array.isArray(existingData) && Array.isArray(receivedData)
    ? receivedData.filter(
        (receivedItem: Product) =>
          existingData.findIndex(
            (existingItem: Product) =>
              existingItem.productId === receivedItem.productId
          ) === -1
      )
    : [];
};

const firstPage = 1;
const pageSize = 20;

const ProductCatalog: React.FC = () => {
  const baseUrl = `${process.env.REACT_APP_REST_BASE_URL}/products`;
  const { data, isLoading, error, setUrl } = useDataApi(undefined);
  const [products, setProducts] = useState<Product[]>([]);
  const [search, setSearch] = useState<ScannerInputValue | undefined>(
    undefined
  );
  const [page, setPage] = useState(firstPage);
  const [hasMorePage, setHasMorePage] = useState(false);
  const previousUrl = useRef<string | undefined>(undefined);
  const { cart, add: addProductItemToCart } = useCart();
  const { settings } = useSettings();
  const { searchValue, isOffsite, setSearchValue } = useApp();
  const { coopInfo } = useDealer();

  useEffect(() => {
    console.log(
      "ProductCatalog useEffect(() => {}, [searchValue = %o, isOffsite = %o])",
      searchValue,
      isOffsite
    ); // !!!
    setProducts([]);
    setPage(firstPage);
    setSearch(searchValue);
    fetchData(firstPage, searchValue);
  }, [searchValue, isOffsite]);

  const incrementPage = () => {
    const newPage = page + 1;
    setPage(newPage);
    fetchData(newPage, search);
  };

  const fetchData = (page: number, search?: ScannerInputValue): void => {
    const newUrl = `${baseUrl}?pageNum=${page}&pageSize=${pageSize}${
      !!search?.value ? `&search=${search.value}` : ""
    }&offsite=${isOffsite ? "true" : "false"}`;

    // if (previousUrl.current === newUrl) {
    //   return;
    // }

    previousUrl.current = newUrl;
    setUrl(newUrl);
  };

  useEffect(() => {
    fetchData(page);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isLoading || !data) {
      return;
    }

    const newData = getNewData(products, data.data);
    if (newData.length) {
      const newProducts = [...products, ...newData];
      setProducts(newProducts);

      if (
        newProducts.length === 1 &&
        newProducts[0].sku === search?.value &&
        (((search?.isCode || !isOffsite) &&
          (CartHelper.isEmpty(cart) || !CartHelper.hasOffsiteProducts(cart))) ||
          ((!search?.isCode || isOffsite) &&
            (CartHelper.isEmpty(cart) || CartHelper.hasOffsiteProducts(cart))))
      ) {
        onProductItemClick(newProducts[0]);

        if (search?.isCode) {
          setSearchValue?.(scannerInputValueInitial);
        } else {
          setSearch(scannerInputValueInitial);
        }
      }
    }

    setHasMorePage(data?.totalPages > data?.page);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, data]);

  const onProductItemClick = async (product?: Product) => {
    const isCartEmpty = CartHelper.isEmpty(cart);

    if (
      !product ||
      (!isCartEmpty &&
        ((product.offsite && !CartHelper.hasOffsiteProducts(cart)) ||
          (!product.offsite && CartHelper.hasOffsiteProducts(cart))))
    ) {
      return;
    }

    addProductItemToCart?.(
      product,
      settings?.shippingFee,
      settings?.claimFee,
      // product.offsite
      //   ? coopInfo?.reimbursementRateOffSite
      //   : coopInfo?.reimbursementRateInStore,
      settings?.coopEligible,
      settings?.couponAmount
    );
  };

  const handleSearchChange = (value?: ScannerInputValue) => {
    setProducts([]);
    setPage(firstPage);
    setSearch(value);
    fetchData(firstPage, value);
  };

  return (
    <S.ProductCatalogContainer>
      <S.ProductCatalogSearch>
        <S.ProductCatalogSearchLabelContainer>
          <S.ProductCatalogSearchLabel>
            Product Catalog
          </S.ProductCatalogSearchLabel>
          <S.ProductCatalogSearchSubLabel>
            Items will be shipped to the buyer from offsite
          </S.ProductCatalogSearchSubLabel>
        </S.ProductCatalogSearchLabelContainer>
        {!isNaN(settings?.scannerInputDebounceWait as number) ? (
          <ScannerInput
            wait={settings?.scannerInputDebounceWait}
            placeholder="Search the catalog"
            searchIcon
            searchIconPosition="start"
            onChange={handleSearchChange}
            searchValue={search}
          />
        ) : undefined}
      </S.ProductCatalogSearch>
      <S.ProductCatalogInfiniteScrollContainer>
        <InfiniteScroll
          dataLength={products?.length || 0}
          next={incrementPage}
          hasMore={hasMorePage || false}
          loader={undefined}
        >
          {!error ? (
            !isLoading ? (
              <ProductTable
                items={products}
                defaultProductImageUrl={settings?.defaultProductImageUrl}
                coopEligible={
                  // isOffsite
                  //   ? coopInfo?.reimbursementRateOffSite
                  //   : coopInfo?.reimbursementRateInStore
                  settings?.coopEligible
                }
                onItemClick={onProductItemClick}
              />
            ) : (
              <CircularIndeterminate />
            )
          ) : (
            <ErrorMessage errorMessage={formatAxiosError(error)} />
          )}
        </InfiniteScroll>
      </S.ProductCatalogInfiniteScrollContainer>
    </S.ProductCatalogContainer>
  );
};

export default ProductCatalog;
